import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, Image, Trash2, Save, RotateCcw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useSiteConfig } from '@/hooks/useSiteConfig';
import { useToast } from '@/hooks/use-toast';

const AdminLogoManagement = () => {
  const [uploading, setUploading] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { updateConfiguration, getConfiguration } = useSiteConfig();
  const { toast } = useToast();

  const currentLogoUrl = getConfiguration('site_logo_url') || '';

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid File Type",
        description: "Please select an image file (PNG, JPG, SVG, etc.)",
        variant: "destructive",
      });
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please select an image smaller than 5MB",
        variant: "destructive",
      });
      return;
    }

    setLogoFile(file);

    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!logoFile) return;

    setUploading(true);
    try {
      // Upload to Supabase Storage
      const fileExt = logoFile.name.split('.').pop();
      const fileName = `logo-${Date.now()}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('user-images')
        .upload(filePath, logoFile);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('user-images')
        .getPublicUrl(filePath);

      // Update site configuration
      await updateConfiguration('site_logo_url', publicUrl);

      // Clear preview and file
      setLogoFile(null);
      setLogoPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      toast({
        title: "Logo Uploaded",
        description: "Website logo has been updated successfully.",
      });

    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        title: "Upload Failed",
        description: "Failed to upload logo. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!confirm('Are you sure you want to remove the current logo?')) return;

    try {
      await updateConfiguration('site_logo_url', '');
      toast({
        title: "Logo Removed",
        description: "Website logo has been removed. Default logo will be used.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove logo. Please try again.",
        variant: "destructive",
      });
    }
  };

  const resetToDefault = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <Image className="w-5 h-5" />
            <span>Logo Management</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Logo Display */}
          <div className="space-y-2">
            <Label className="text-gray-300">Current Website Logo</Label>
            <div className="p-4 border border-white/20 rounded-lg bg-white/5">
              {currentLogoUrl ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <img 
                      src={currentLogoUrl} 
                      alt="Current Logo" 
                      className="h-12 w-auto max-w-[200px] object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <div className="text-sm text-gray-400">
                      Logo is active
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRemoveLogo}
                    className="text-red-400 border-red-400 hover:bg-red-400/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-2 text-gray-400 py-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">BG</span>
                  </div>
                  <span>Default logo (no custom logo uploaded)</span>
                </div>
              )}
            </div>
          </div>

          {/* Upload New Logo */}
          <div className="space-y-4">
            <Label className="text-gray-300">Upload New Logo</Label>
            
            {/* File Input */}
            <div className="space-y-4">
              <Input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="bg-white/5 border-white/20 text-white file:bg-blue-600 file:text-white file:border-0 file:rounded-md file:px-4 file:py-2 file:mr-4"
              />
              
              {/* Preview */}
              {logoPreview && (
                <div className="space-y-2">
                  <Label className="text-gray-300">Preview</Label>
                  <div className="p-4 border border-white/20 rounded-lg bg-white/5">
                    <div className="flex items-center justify-between">
                      <img 
                        src={logoPreview} 
                        alt="Logo Preview" 
                        className="h-12 w-auto max-w-[200px] object-contain"
                      />
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={resetToDefault}
                          className="text-gray-400 border-gray-400 hover:bg-gray-400/10"
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                        <Button
                          onClick={handleUpload}
                          disabled={uploading}
                          size="sm"
                          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                        >
                          {uploading ? (
                            <>
                              <div className="custom-loader w-4 h-4 mr-2" style={{width: '16px', height: '32px'}}></div>
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              Upload Logo
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Upload Instructions */}
            <div className="text-sm text-gray-400 space-y-1">
              <p>• Supported formats: PNG, JPG, JPEG, SVG, WebP</p>
              <p>• Maximum file size: 5MB</p>
              <p>• Recommended size: 200x50px (or similar ratio)</p>
              <p>• Transparent backgrounds work best</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogoManagement;